import { Point as FabricPoint, util } from 'fabric';
import React from 'react';

import { Coords } from 'editor/src/store/design/types';
import getSettingsValue from 'editor/src/store/editor/selector/getSettingsValue';
import { SettingsProperty } from 'editor/src/store/editor/types';
import { useSelector } from 'editor/src/store/hooks';

import { convertMmToUnit } from 'editor/src/util/math';
import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import { CanvasRotation } from '../types';
import useForceUpdateOnViewportChange from '../useForceUpdateOnViewportChange';

import useRulerGuides from './context';
import { documentGuideAxis, GuideAxis, guideSegment, RulerGuide } from './geometry';

interface Props {
  measurementOrigin?: { left: number; top: number };
  coords: Coords;
  width: number;
  height: number;
  rotation: CanvasRotation;
  draft?: RulerGuide;
  onPointerDown: (event: React.PointerEvent, axis: GuideAxis, guide: RulerGuide) => void;
}

export default function RulerGuides({
  coords,
  width,
  height,
  rotation,
  draft,
  onPointerDown,
  measurementOrigin,
}: Props) {
  const canvas = useFabricCanvas();
  const { mm2px } = useFabricUtils();
  const { guides } = useRulerGuides();
  const unit = useSelector((state) => getSettingsValue(state, SettingsProperty.units));
  // A selected element's controls win over the guide line they sit under — a guide lying on the
  // design edge would otherwise swallow the press on a rotate or resize handle, and the element
  // could not be manipulated at all. The label by the ruler stays live, so a guide can still be
  // moved without clearing the selection first.
  const elementSelected = useSelector(
    (state) => state.editor.selectedElementUuids.length > 0 || !!state.editor.cropModeElementUuid,
  );
  useForceUpdateOnViewportChange();
  // Moving the captured SVG node in the DOM interrupts the browser's pointer capture.
  const visible = guides.map((guide) => (guide.id === draft?.id ? draft : guide));
  if (draft && !guides.some((guide) => guide.id === draft.id)) {
    visible.push(draft);
  }

  return (
    <svg
      data-ruler-guides
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      {visible.map((guide) => {
        const [from, to] = guideSegment(guide, coords, width, height, mm2px, rotation).map((point) =>
          util.transformPoint(new FabricPoint(point), canvas.viewportTransform),
        );
        const vertical = documentGuideAxis(guide.axis, rotation.angleDeg) === 'vertical';
        // Extend both the visible line and its drag target across the editing viewport.
        // This keeps guides reachable between the design edge and the ruler.
        if (vertical) {
          from.y = 0;
          to.y = canvas.getHeight();
        } else {
          from.x = 0;
          to.x = canvas.getWidth();
        }
        const cursor = vertical ? 'ew-resize' : 'ns-resize';
        const labelPosition = vertical
          ? Math.max(56, Math.min(canvas.getWidth() - 36, from.x))
          : Math.max(56, Math.min(canvas.getHeight() - 36, from.y));
        return (
          <g key={guide.id} data-guide-axis={guide.axis} data-guide-position={guide.position}>
            <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#E500E5" strokeWidth={1} />
            <line
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke="transparent"
              strokeWidth={8}
              style={{
                pointerEvents: elementSelected ? 'none' : 'stroke',
                cursor,
              }}
              onPointerDown={(event) => onPointerDown(event, guide.axis, guide)}
            />
            <g
              data-guide-label={guide.id}
              transform={vertical ? `translate(${labelPosition} 10)` : `translate(10 ${labelPosition}) rotate(-90)`}
              style={{ pointerEvents: 'auto', cursor }}
              onPointerDown={(event) => onPointerDown(event, guide.axis, guide)}
            >
              <rect x={-35} y={-9} width={70} height={18} rx={3} fill="white" stroke="#A100A1" />
              <text textAnchor="middle" dominantBaseline="central" fontSize={11} fill="#750075">
                {Number(
                  convertMmToUnit(
                    guide.position -
                      (guide.axis === 'vertical' ? measurementOrigin?.left || 0 : measurementOrigin?.top || 0),
                    unit,
                  ).toFixed(2),
                )}{' '}
                {unit}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}
